import { useState, useEffect, useCallback, useRef } from 'react';
import { processApiResponse } from './availabilityUtils';
import { getAllFacilityIds, isDailyFacility } from './facilityConfig';

// API configuration
const API_BASE_URL = 'https://mansion-facility-wtr-reservation-api.info1499.workers.dev';
const API_ENDPOINTS = {
  BATCH: '/api/v1/facilities/batch/availability',
  FACILITY: '/api/v1/facilities'
};

// Cache configuration
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
const REFETCH_INTERVAL = 5 * 60 * 1000; // 5 minutes
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 1000; // 1 second

// In-memory cache
const cache = new Map();

// Hook to fetch facility availability data
export const useFacilityAvailability = (options = {}) => {
  const {
    facilityIds = getAllFacilityIds(),
    days = 30,
    autoRefetch = true,
    refetchInterval = REFETCH_INTERVAL,
    onError = null,
    onSuccess = null
  } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  
  const intervalRef = useRef(null);
  const isMountedRef = useRef(true);
  const retryCountRef = useRef(0);

  // Generate cache key
  const getCacheKey = useCallback(() => {
    const sortedIds = [...facilityIds].sort();
    return `${sortedIds.join(',')}-${days}`;
  }, [facilityIds, days]);

  // Check if cached data is still valid
  const isCacheValid = useCallback((cachedItem) => {
    if (!cachedItem) return false;
    const now = Date.now();
    return now - cachedItem.timestamp < CACHE_DURATION;
  }, []);

  // Get data from cache
  const getCachedData = useCallback(() => {
    const cacheKey = getCacheKey();
    const cachedItem = cache.get(cacheKey);
    
    if (cachedItem && isCacheValid(cachedItem)) {
      return cachedItem.data;
    }
    
    return null;
  }, [getCacheKey, isCacheValid]);

  // Save data to cache
  const setCachedData = useCallback((data) => {
    const cacheKey = getCacheKey();
    cache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });
  }, [getCacheKey]);

  // Clean up old cache entries
  const cleanupCache = useCallback(() => {
    const now = Date.now();
    for (const [key, value] of cache.entries()) {
      if (now - value.timestamp > CACHE_DURATION * 2) {
        cache.delete(key);
      }
    }
  }, []);

  // Fetch data from API
  const fetchData = useCallback(async (isRetry = false) => {
    if (!isMountedRef.current) return;

    // Check cache first
    if (!isRetry) {
      const cachedData = getCachedData();
      if (cachedData) {
        setData(cachedData);
        setLastFetchTime(Date.now());
        if (onSuccess) onSuccess(cachedData);
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      // TODO: Use batch endpoint when available
      // For now, fetch facilities individually
      const facilityPromises = facilityIds.map(async (facilityId) => {
        try {
          // Determine the correct endpoint based on facility type
          const isDaily = isDailyFacility(facilityId);
          const endpoint = isDaily 
            ? `${API_BASE_URL}${API_ENDPOINTS.FACILITY}/${facilityId}/daily-availability?days=${days}`
            : `${API_BASE_URL}${API_ENDPOINTS.FACILITY}/${facilityId}/availability?days=${days}`;
          
          const response = await fetch(endpoint, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          });

          if (!response.ok) {
            throw new Error(`Failed to fetch ${facilityId}: ${response.status}`);
          }

          const data = await response.json();
          return { facilityId, data, isDaily };
        } catch (err) {
          console.error(`Error fetching ${facilityId}:`, err);
          return { facilityId, error: err.message };
        }
      });

      const results = await Promise.all(facilityPromises);
      
      // Convert to batch format
      const batchResponse = {
        lastUpdated: new Date().toISOString(),
        facilities: {}
      };
      
      results.forEach(({ facilityId, data, error, isDaily }) => {
        if (error) {
          batchResponse.facilities[facilityId] = { error };
        } else {
          // The API response has the structure { facility, lastUpdated, days }
          // Pass the entire response data as processApiResponse expects it
          batchResponse.facilities[facilityId] = data;
        }
      });

      const processedData = processApiResponse(batchResponse);
      
      if (isMountedRef.current) {
        setData(processedData);
        setCachedData(processedData);
        setLastFetchTime(Date.now());
        setError(null);
        retryCountRef.current = 0;
        
        if (onSuccess) onSuccess(processedData);
        
        // Cleanup old cache entries
        cleanupCache();
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage = err.message || 'Failed to fetch facility availability';
        setError(errorMessage);
        
        // Retry logic
        if (retryCountRef.current < MAX_RETRY_ATTEMPTS) {
          retryCountRef.current++;
          setTimeout(() => {
            if (isMountedRef.current) {
              fetchData(true);
            }
          }, RETRY_DELAY * retryCountRef.current);
        } else {
          setLoading(false);
          if (onError) onError(err);
        }
      }
    } finally {
      if (isMountedRef.current && retryCountRef.current === 0) {
        setLoading(false);
      }
    }
  }, [facilityIds, days, getCachedData, setCachedData, cleanupCache, onError, onSuccess]);

  // Refetch data
  const refetch = useCallback(() => {
    retryCountRef.current = 0;
    fetchData();
  }, [fetchData]);

  // Set up initial fetch and auto-refetch
  useEffect(() => {
    isMountedRef.current = true;
    
    // Initial fetch
    fetchData();

    // Set up auto-refetch interval
    if (autoRefetch && refetchInterval > 0) {
      intervalRef.current = setInterval(() => {
        if (isMountedRef.current) {
          fetchData();
        }
      }, refetchInterval);
    }

    // Cleanup
    return () => {
      isMountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchData, autoRefetch, refetchInterval]);

  // Get data for a specific facility
  const getFacilityData = useCallback((facilityId) => {
    if (!data || !data.facilities) return null;
    return data.facilities[facilityId] || null;
  }, [data]);

  // Check if data is stale
  const isStale = useCallback(() => {
    if (!lastFetchTime) return true;
    return Date.now() - lastFetchTime > CACHE_DURATION;
  }, [lastFetchTime]);

  return {
    data,
    loading,
    error,
    refetch,
    lastFetchTime,
    isStale: isStale(),
    getFacilityData
  };
};

// Hook to fetch data for a single facility
export const useSingleFacilityAvailability = (facilityId, options = {}) => {
  const { days = 30, ...otherOptions } = options;
  
  return useFacilityAvailability({
    ...otherOptions,
    facilityIds: [facilityId],
    days
  });
};

// Hook to manage facility selection and data
export const useFacilitySelection = (initialFacilityId = null) => {
  const [selectedFacilityId, setSelectedFacilityId] = useState(initialFacilityId);
  const [selectedDate, setSelectedDate] = useState(null);
  
  const {
    data,
    loading,
    error,
    refetch,
    getFacilityData
  } = useFacilityAvailability();
  
  const selectedFacilityData = selectedFacilityId ? getFacilityData(selectedFacilityId) : null;
  
  return {
    // Selection state
    selectedFacilityId,
    setSelectedFacilityId,
    selectedDate,
    setSelectedDate,
    
    // Data
    allData: data,
    selectedFacilityData,
    
    // Loading state
    loading,
    error,
    refetch
  };
};